import React, { useEffect, useState } from "react";
import * as api from "../../../../api/generated-api";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import Button from "../../../common/forms/button";
import DKFormPercentageInput from "../../../common/widgets/formVariablesWidget/DKFormPercentageInput";
import { seriesById } from "../../../../api/deals/deals";
import { currencyFormat, formatNumber } from "../../../../helpers/utils";
import { DealLegalEntitySelect } from "../DealLegalEntitySelect";
import DKFormMoneyInput from "../../../common/widgets/formVariablesWidget/DKFormMoneyInput";
import DKFormTextInput from "../../../common/widgets/formVariablesWidget/DKFormTextInput";
import DKFormSelect from "../../../common/forms/DKFormSelect";
import { DealPageActionButtonInfo } from "../../dealsPageLayout/DealsPageHeader/DealsPageHeader";
import { DKGridColumn } from "../../../common/grid/DKGridColumn";
import { DKGridTable } from "../../../common/grid/DKGridTable";
import { InvestmentSignatureBlockRowAction } from "../InvestmentSignatureBlockRowAction";
import { DKGridFooterColumn } from "../../../common/grid/DKGridFooterColumn";
import InvestorRowCellWrapper from "../InvestorRowCellWrapper";
import RemoveIcon from "../../../common/icons/removeIcon";
import { DealLegalEntityInvestmentFormEntry } from "../DealInvestorDetailPage";
import InvestorActionCellWrapper from "../InvestorActionCellWrapper";

type SAFEConversionsGridProps = {
  items: api.DealLegalEntityInvestment[];
  dealInvestorId: number;
  canEditInvestorInfo: boolean;
  editRowIndex?: number;
  onCancel: () => void;
  onSave: (item: api.DealLegalEntityInvestment) => void;
  onEdit: (index: number) => void;
  onAdd: () => void;
  onRemove: (
    dealLegalEntityInvestmentForEntry: DealLegalEntityInvestmentFormEntry
  ) => void;
  deal: api.Deal;
  dealLegalEntitiesOfInvestor: api.DealLegalEntity[];
};

export const SAFEConversionsGrid = (props: SAFEConversionsGridProps) => {
  const {
    deal,
    editRowIndex,
    dealLegalEntitiesOfInvestor,
    items,
    dealInvestorId,
    canEditInvestorInfo,
    onCancel,
    onSave,
    onEdit,
    onAdd,
    onRemove,
  } = props;
  const dealSeries: api.DealSeries[] = deal.series;

  const theme = useTheme();

  const getSelectedDealSeriesById = (dealSeriesId: number) => {
    return dealSeries.find((series) => {
      if (series.id == dealSeriesId) {
        return series;
      }
    });
  };

  const calculateShares = (
    dealSeriesId: number,
    purchaseAmount: number
  ): number => {
    const selectedSeries = getSelectedDealSeriesById(dealSeriesId);
    if (selectedSeries) {
      return Math.floor(purchaseAmount / selectedSeries.pricePerShare);
    }
  };

  const [rows, setRows] = useState<api.DealLegalEntityInvestment[]>(items);

  useEffect(() => {
    setRows(items);
  }, [items]);

  const updateRowValue = (
    index: number,
    value: api.DealLegalEntityInvestment
  ) => {
    const newRows = [...rows];
    newRows[index] = value;
    setRows(newRows);
  };

  const actionButtons: DealPageActionButtonInfo[] = [];

  if (editRowIndex != undefined) {
    actionButtons.push({
      dataTestId: "cancel-safe-conversions",
      label: "cancel",
      onClick: () => {
        onCancel();
      },
    });
    actionButtons.push({
      dataTestId: "save-safe-conversions",
      label: "save",
      onClick: () => {
        onSave(rows[editRowIndex]);
      },
    });
  } else {
    if (canEditInvestorInfo) {
      actionButtons.push({
        dataTestId: "add-safe-conversions",
        label: "add",
        onClick: () => {
          onAdd();
        },
      });
    }
  }

  const Footer = () => {
    const totalShares = rows.reduce((previousValue, currentValue) => {
      const currentShares =
        Number.isNaN(currentValue.shares) || currentValue.shares == undefined
          ? 0
          : currentValue.shares;
      return currentShares + previousValue;
    }, 0);

    const totalPurchaseAmount = rows.reduce((previousValue, currentValue) => {
      const currentPurchaseAmount =
        Number.isNaN(currentValue.purchaseAmount) ||
        currentValue.purchaseAmount == undefined
          ? 0
          : currentValue.purchaseAmount;
      return currentPurchaseAmount + previousValue;
    }, 0);

    if (rows.length == 0) {
      return null;
    }

    return (
      <>
        <Grid container spacing={2}>
          <DKGridFooterColumn width={3} title={"Total"} />
          <DKGridFooterColumn
            dataTestId={"SAFE-investments-total-purchase-amount"}
            width={2}
            title={currencyFormat(totalPurchaseAmount)}
          />
          <DKGridFooterColumn width={5} title={""} />
          <DKGridFooterColumn
            dataTestId={"SAFE-investments-total-shares"}
            width={1}
            title={formatNumber(totalShares)}
          />
          <DKGridFooterColumn width={1} title={""} />
        </Grid>
      </>
    );
  };

  return (
    <DKGridTable
      title={"SAFE Conversions"}
      rowsLength={rows.length}
      actionButtons={actionButtons}
      footer={<Footer />}
    >
      <DKGridColumn width={3} title={"SAFE Holder Entity Legal Name"}>
        {rows.map((item, itemIndex) => {
          if (editRowIndex != itemIndex) {
            return (
              <InvestorRowCellWrapper key={"name-" + itemIndex}>
                <Typography variant={"body1"}>{item.name}</Typography>
              </InvestorRowCellWrapper>
            );
          } else {
            return (
              <DealLegalEntitySelect
                dataTestId={"SAFE-investment-name-field-" + itemIndex}
                key={"name-" + itemIndex}
                dealLegalEntities={dealLegalEntitiesOfInvestor}
                selectedDealLegalEntityId={item.dealInvestorMemberSignerId}
                onChange={(event) => {
                  updateRowValue(itemIndex, {
                    ...rows[itemIndex],
                    dealInvestorMemberSignerId: Number(event.target.value),
                  });
                }}
              />
            );
          }
        })}
      </DKGridColumn>
      <DKGridColumn width={2} title={"Purchase Amount"}>
        {rows.map((item, itemIndex) => {
          if (editRowIndex != itemIndex) {
            return (
              <InvestorRowCellWrapper key={"purchaseAmount-" + itemIndex}>
                <Typography variant={"body1"}>
                  {currencyFormat(item.purchaseAmount)}
                </Typography>
              </InvestorRowCellWrapper>
            );
          } else {
            return (
              <DKFormMoneyInput
                dataTestId={"SAFE-investment-purchaseAmount-field-" + itemIndex}
                key={"purchaseAmount-" + itemIndex}
                name={"purchaseAmount"}
                label={""}
                value={item.purchaseAmount}
                onChange={(value) => {
                  const shares = calculateShares(
                    rows[itemIndex].dealSeriesId,
                    value
                  );

                  updateRowValue(itemIndex, {
                    ...rows[itemIndex],
                    purchaseAmount: value,
                    shares,
                  });
                }}
              />
            );
          }
        })}
      </DKGridColumn>
      <DKGridColumn width={2} title={"Valuation Cap"}>
        {rows.map((item, itemIndex) => {
          if (editRowIndex != itemIndex) {
            return (
              <InvestorRowCellWrapper key={"valuationCap-" + itemIndex}>
                <Typography variant={"body1"}>
                  {currencyFormat(item.valuationCap)}
                </Typography>
              </InvestorRowCellWrapper>
            );
          } else {
            return (
              <DKFormMoneyInput
                dataTestId={"SAFE-investment-valuationCap-field-" + itemIndex}
                key={"valuationCap-" + itemIndex}
                name={"valuationCap"}
                label={""}
                value={item.valuationCap}
                onChange={(value) => {
                  updateRowValue(itemIndex, {
                    ...rows[itemIndex],
                    valuationCap: value,
                  });
                }}
              />
            );
          }
        })}
      </DKGridColumn>
      <DKGridColumn width={1} title={"Discount"}>
        {rows.map((item, itemIndex) => {
          if (editRowIndex != itemIndex) {
            return (
              <InvestorRowCellWrapper key={"discount-" + itemIndex}>
                <Typography variant={"body1"}>{item.discount}%</Typography>
              </InvestorRowCellWrapper>
            );
          } else {
            return (
              <DKFormPercentageInput
                dataTestId={"SAFE-investment-discount-field-" + itemIndex}
                key={"discount-" + itemIndex}
                name={"discount"}
                label={""}
                value={item.discount}
                onChange={(value) => {
                  updateRowValue(itemIndex, {
                    ...rows[itemIndex],
                    discount: value,
                  });
                }}
              />
            );
          }
        })}
      </DKGridColumn>
      <DKGridColumn width={2} title={"Stock Series"}>
        {rows.map((item, itemIndex) => {
          if (editRowIndex != itemIndex) {
            return (
              <InvestorRowCellWrapper key={"series-" + itemIndex}>
                <Typography variant={"body1"}>
                  {seriesById(deal, item.dealSeriesId)?.name}
                </Typography>
              </InvestorRowCellWrapper>
            );
          } else {
            return (
              <DKFormSelect
                dataTestId={"SAFE-investment-series-field-" + itemIndex}
                key={"series-" + itemIndex}
                label={""}
                selectedValue={item.dealSeriesId}
                onChange={(event) => {
                  const dealSeriesId = Number(event.target.value);
                  const shares = calculateShares(
                    dealSeriesId,
                    item.purchaseAmount
                  );

                  updateRowValue(itemIndex, {
                    ...item,
                    dealSeriesId,
                    shares,
                  });
                }}
                items={dealSeries.map((series) => {
                  return {
                    value: series.id,
                    label: series.name,
                  };
                })}
              />
            );
          }
        })}
      </DKGridColumn>
      <DKGridColumn width={1} title={"Shares"}>
        {rows.map((item, itemIndex) => {
          if (editRowIndex != itemIndex) {
            return (
              <InvestorRowCellWrapper key={"shares-" + itemIndex}>
                <Typography variant={"body1"}>
                  {formatNumber(item.shares)}
                </Typography>
              </InvestorRowCellWrapper>
            );
          } else {
            return (
              <DKFormTextInput
                key={"shares-" + itemIndex}
                name={"shares"}
                label={""}
                value={item.shares}
                disabled={true}
              />
            );
          }
        })}
      </DKGridColumn>
      <DKGridColumn width={1} title={" "}>
        {rows.map((item, itemIndex) => {
          if (editRowIndex != itemIndex) {
            return (
              <InvestorRowCellWrapper key={"row-actions-" + itemIndex}>
                <InvestorActionCellWrapper>
                  <Button
                    data-testid={"edit-safe-conversion-button"}
                    type="ghost"
                    size="small"
                    onClick={() => {
                      onEdit(itemIndex);
                    }}
                    itemLeft={undefined}
                  >
                    edit
                  </Button>
                  <Box ml={1}>
                    <InvestmentSignatureBlockRowAction
                      dataTestId={
                        "SAFE-investment-signature-block-link-" + itemIndex
                      }
                      orgId={deal.organizationId}
                      dealId={deal.id}
                      dealInvestorId={dealInvestorId}
                      dealLegalEntityInvestmentId={item.id}
                      itemIndex={itemIndex}
                    />
                  </Box>
                </InvestorActionCellWrapper>
              </InvestorRowCellWrapper>
            );
          } else {
            return (
              <InvestorRowCellWrapper key={"row-actions-" + itemIndex} editMode>
                <InvestorActionCellWrapper>
                  <RemoveIcon
                    data-testid={"row-action-delete-SAFE-investment"}
                    fill={
                      !item.wireDateReceived
                        ? theme.palette.secondary.darker
                        : theme.palette.tertiary.dark
                    }
                    onClick={() => {
                      !item.wireDateReceived ? onRemove(item) : undefined;
                    }}
                  />
                </InvestorActionCellWrapper>
              </InvestorRowCellWrapper>
            );
          }
        })}
      </DKGridColumn>
    </DKGridTable>
  );
};
